import React, { useState, useEffect, useCallback } from 'react';
import ReactPlayer from 'react-player';
import { Box, CircularProgress, Typography, Button, styled, Alert } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { Question } from '../../types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReplayIcon from '@mui/icons-material/Replay';
import { videos } from '../../services/api';  

const PlayerWrapper = styled(Box)<{ theme?: Theme }>(({ theme }) => ({
  position: 'relative',
  paddingTop: '56.25%', // 16:9 aspect ratio
  maxWidth: '1000px', 
  width: '100%',
  margin: '0 auto', 
}));

const StyledReactPlayer = styled(ReactPlayer)({
  position: 'absolute',
  top: 0,
  left: 0,
});

const CompletionIndicator = styled(Box)<{ theme?: Theme }>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme?.spacing(1),
  marginTop: theme?.spacing(2),
  color: theme?.palette.success.main,
}));

interface Props {
  question: Question;
  onComplete?: () => void;
  isSubmitting?: boolean;
  onValidationChange?: (isValid: boolean) => void;
}

export const VideoPlayerQuestion: React.FC<Props> = ({
  question,
  onComplete,
  isSubmitting = false,
  onValidationChange,
}) => {
  const [isCompleted, setIsCompleted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const fetchVideoUrl = useCallback(async () => {
    if (!question.video_url) {
      console.log('No video URL provided');
      setErrorMessage('No video URL provided');
      setHasError(true);
      setIsLoading(false);
      return;
    }

    console.log('Fetching video URL:', question.video_url);
    setIsLoading(true);
    setHasError(false);
    setErrorMessage('');

    try {
      // Extract just the filename from the URL
      let videoKey = question.video_url;
      if (videoKey.startsWith('http')) {
        const url = new URL(videoKey);
        const pathParts = url.pathname.split('/');
        videoKey = pathParts[pathParts.length - 1];
      }
      console.log('Using video key:', videoKey);

      const url = await videos.getVideoUrl(videoKey);
      console.log('Successfully fetched signed URL:', url);
      console.log('URL length:', url.length);
      console.log('URL starts with:', url.substring(0, 50) + '...');
      setVideoUrl(url);
    } catch (error) {
      console.error('Error fetching video URL:', error);
      setHasError(true);
      setVideoUrl('');
      // Handle the unknown error type properly
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setErrorMessage(`Failed to load video: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  }, [question.video_url]);

  useEffect(() => {
    const controller = new AbortController();
    
    fetchVideoUrl();
    
    return () => {
      controller.abort();
    };
  }, [fetchVideoUrl]);

  useEffect(() => {
    // Update parent component about validation state
    onValidationChange?.(isCompleted);
  }, [isCompleted, onValidationChange]);

  const handleVideoEnd = () => {
    setIsCompleted(true);
    if (onComplete) {
      onComplete();
    }
  };

  const handleReady = () => {
    setIsLoading(false);
  };

  const handleError = (error: any) => {
    console.error('Video player error:', error);
    // Log more detailed information about the error
    if (error && error.target) {
      console.error('Video element error details:', {
        error: error.target.error,
        networkState: error.target.networkState,
        readyState: error.target.readyState,
        currentSrc: error.target.currentSrc,
        videoWidth: error.target.videoWidth,
        videoHeight: error.target.videoHeight
      });
    }
    setHasError(true);
    setErrorMessage(`Error playing video: ${error?.message || 'Unknown error'}. Please check your connection and try again.`);
    setVideoUrl('');
  };

  const handleRetry = () => {
    fetchVideoUrl();
  };

  if ((!videoUrl && !isLoading) || hasError) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '300px',
        gap: 2,
        p: 3,
        textAlign: 'center',
        border: '1px dashed',
        borderColor: 'error.light',
        borderRadius: 1,
        bgcolor: 'background.paper'
      }}>
        <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
          {errorMessage || 'Failed to load video'}
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={handleRetry}
          startIcon={<ReplayIcon />}
          sx={{ mt: 1 }}
        >
          Retry Loading Video
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      minWidth: '800px', 
      maxWidth: '1200px', 
      margin: '0 auto' 
    }}>
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      )}
      
      {videoUrl && (
        <PlayerWrapper>
          <StyledReactPlayer
            url={videoUrl}
            controls
            width="100%"
            height="100%"
            onEnded={handleVideoEnd}
            onError={handleError}
            onReady={handleReady}
            onBuffer={() => console.log('Video buffering...')}
            onBufferEnd={() => console.log('Video buffering ended')}
            onProgress={(state) => console.log('Video progress:', state.played)}
            config={{
              file: {
                attributes: {
                  controlsList: 'nodownload',
                  disablePictureInPicture: true,
                },
                forceVideo: true,
              },
            }}
          />
        </PlayerWrapper>
      )}
      
      {hasError && (
        <Box sx={{ color: 'error.main', mt: 2, textAlign: 'center' }}>
          {errorMessage}
        </Box>
      )}
      
      {isCompleted && (
        <CompletionIndicator>
          <CheckCircleIcon />
          <Typography>Video completed</Typography>
        </CompletionIndicator>
      )}
    </Box>
  );
};

export default VideoPlayerQuestion;
